// src/pages/financials/QboStatementTab.tsx
//
// Profit & Loss and Balance Sheet from QuickBooks Online's own reports
// (feature financials.qbo, agent 3.24.0+), keeping the QuickBooks account
// tree: the Level control shows the statement at level 1 (sections only)
// through the deepest sub-account. A parent account's line carries its
// QuickBooks total; its direct postings show as a child line of the same name.
// P&L: any date range by month / quarter / total. Balance sheet: as of any
// date, optionally with the prior month-ends beside it.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileSpreadsheet as SheetIcon, RefreshCw, Scale } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { QboReport, QboReportLine } from '../../types/qboReports';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { ExportButtons } from '../analytics/payables/apShared';

const fmt0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

function Amt({ v, bold = false }: { v: number; bold?: boolean }) {
  if (Math.abs(v) < 0.005) return <span className="text-slate-600">–</span>;
  if (v < 0) return <span className={`text-red-400 ${bold ? 'font-semibold' : ''}`}>({fmt0.format(Math.abs(v))})</span>;
  return <span className={bold ? 'font-semibold' : ''}>{fmt0.format(v)}</span>;
}

const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-indigo-500/15 text-indigo-200 border-indigo-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;
const labelCls = 'text-xs font-medium text-slate-400 uppercase tracking-wider';

function iso(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Computed P&L lines and the section they follow.
const PNL_AFTER: Record<string, { key: string; label: string }[]> = {
  cogs: [{ key: 'GrossProfit', label: 'Gross Profit' }],
  expense: [{ key: 'NetOperatingIncome', label: 'Net Operating Income' }],
  otherExpense: [{ key: 'NetOtherIncome', label: 'Net Other Income' }],
};

type Flat = { kind: 'section' | 'row' | 'sectionTotal' | 'summary'; label: string; level: number; isGroup: boolean; amounts: number[]; total: number };

export function QboStatementTab({ kind }: { kind: 'pnl' | 'balanceSheet' }) {
  const isPnl = kind === 'pnl';
  const today = new Date();
  const [from, setFrom] = useState(() => `${today.getFullYear()}-01-01`);
  const [to, setTo] = useState(() => iso(today));
  const [summarize, setSummarize] = useState('month');
  const [months, setMonths] = useState(1);
  const [basis, setBasis] = useState<'accrual' | 'cash'>('accrual');
  const [level, setLevel] = useState(isPnl ? 3 : 4);

  const q = useQuery({
    queryKey: ['qboReport', kind, from, to, summarize, months, basis],
    queryFn: () => isPnl
      ? api.getQboPnl({ from, to, summarize, basis })
      : api.getQboBalanceSheet({ asOf: to, months, basis }),
    staleTime: 5 * 60_000,
    retry: 0,
  });
  const data: QboReport | undefined = q.data;
  const maxLevel = Math.max(1, data?.maxLevel ?? 4);
  const lvl = Math.min(level, maxLevel);

  // Flatten to display rows at the chosen level.
  const flat = useMemo<Flat[]>(() => {
    if (!data) return [];
    const out: Flat[] = [];
    const sum = (key: string, label: string) => {
      const s = data.summaries[key];
      if (s) out.push({ kind: 'summary', label, level: 1, isGroup: false, amounts: s.amounts, total: s.total });
    };
    for (const s of data.sections) {
      if (lvl > 1) {
        out.push({ kind: 'section', label: s.label, level: 1, isGroup: true, amounts: [], total: 0 });
        for (const r of s.rows as QboReportLine[]) {
          if (r.level > lvl) continue;
          out.push({ kind: 'row', label: r.account, level: r.level, isGroup: r.isGroup && r.level < lvl, amounts: r.amounts, total: r.total });
        }
      }
      out.push({ kind: 'sectionTotal', label: lvl > 1 ? `Total ${s.label}` : s.label, level: 1, isGroup: false, amounts: s.amounts, total: s.total });
      if (isPnl) for (const c of PNL_AFTER[s.key] ?? []) sum(c.key, c.label);
    }
    if (isPnl) sum('NetIncome', 'Net Income');
    return out;
  }, [data, lvl, isPnl]);

  const showTotalCol = !!data?.hasTotal;
  const cols = data?.columns ?? [];
  const exportCols: CsvColumns<Flat> = [
    ['Account', r => `${'  '.repeat(Math.max(0, r.level - 1))}${r.label}`],
    ...cols.map((c, i) => [c, (r: Flat) => r.kind === 'section' ? '' : r.amounts[i] ?? 0] as [string, (r: Flat) => string | number]),
    ...(showTotalCol ? [['Total', (r: Flat) => r.kind === 'section' ? '' : r.total] as [string, (r: Flat) => string | number]] : []),
  ];
  const fileBase = isPnl ? `qbo-pnl-${from}-${to}` : `qbo-balance-sheet-${to}`;

  const assets = data?.sections.find(s => s.key === 'assets');
  const le = data?.sections.find(s => s.key === 'liabilitiesAndEquity');
  const balanced = assets && le ? Math.abs(assets.total - le.total) < 1 : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          {isPnl ? <SheetIcon size={20} /> : <Scale size={20} />} {isPnl ? 'Profit & Loss' : 'Balance Sheet'}
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          From QuickBooks Online, with QuickBooks' account levels
          {data && <span className="text-slate-500"> · {isPnl ? `${data.from} → ${data.to}` : `as of ${data.to}`} · {data.basis}</span>}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        {isPnl && <DateField label="From" value={from} onChange={setFrom} />}
        <DateField label={isPnl ? 'To' : 'As of'} value={to} onChange={setTo} />
        {isPnl ? (
          <div className="flex flex-col gap-1">
            <span className={labelCls}>Columns</span>
            <div className="flex gap-1">
              {[['month', 'Months'], ['quarter', 'Quarters'], ['total', 'Total only']].map(([v, l]) => (
                <button key={v} className={pill(summarize === v)} onClick={() => setSummarize(v)}>{l}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <span className={labelCls}>Compare</span>
            <div className="flex gap-1">
              {[[1, 'This date'], [3, '3 month-ends'], [6, '6'], [12, '12']].map(([v, l]) => (
                <button key={v} className={pill(months === v)} onClick={() => setMonths(v as number)}>{l}</button>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Basis</span>
          <div className="flex gap-1">
            <button className={pill(basis === 'accrual')} onClick={() => setBasis('accrual')}>Accrual</button>
            <button className={pill(basis === 'cash')} onClick={() => setBasis('cash')}>Cash</button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={labelCls}>Level</span>
          <div className="flex gap-1">
            {Array.from({ length: maxLevel }, (_, i) => i + 1).map(n => (
              <button key={n} className={pill(lvl === n)} onClick={() => setLevel(n)} title={n === 1 ? 'Sections only' : `Down to level ${n}`}>{n}</button>
            ))}
          </div>
        </div>
        <div className="flex-1" />
        {!isPnl && balanced != null && (
          <span className={`text-xs ${balanced ? 'text-emerald-400' : 'text-red-400'}`}>
            Assets {fmt0.format(assets!.total)} {balanced ? '=' : '≠'} Liabilities + Equity {fmt0.format(le!.total)}
          </span>
        )}
        <ExportButtons what={isPnl ? 'the P&L' : 'the balance sheet'} disabled={!flat.length}
          onCsv={() => exportCsv(fileBase, exportCols, flat)} onXlsx={() => exportXlsx(fileBase, exportCols, flat)} />
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the {isPnl ? 'P&L' : 'balance sheet'} from QuickBooks Online</div>
            <div className="text-xs text-red-300/70 mt-0.5">
              {(q.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? (q.error as Error).message}
            </div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-indigo-400 animate-spin" /></div>
      ) : !q.error && data && (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-auto max-h-[75vh]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-slate-800">
              <tr className="border-b border-slate-700/50 text-slate-400">
                <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left font-medium min-w-[260px]">Account</th>
                {cols.map(c => <th key={c} className="px-3 py-2.5 text-right font-medium whitespace-nowrap">{c}</th>)}
                {showTotalCol && <th className="px-3 py-2.5 text-right font-medium">Total</th>}
              </tr>
            </thead>
            <tbody>
              {flat.map((r, i) => {
                if (r.kind === 'section') {
                  return (
                    <tr key={i} className="bg-slate-800/40">
                      <td colSpan={cols.length + 1 + (showTotalCol ? 1 : 0)} className="sticky left-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-indigo-300">{r.label}</td>
                    </tr>
                  );
                }
                const strong = r.kind !== 'row' || r.isGroup;
                const rowCls = r.kind === 'summary' ? 'border-t-2 border-slate-600/60 bg-slate-800/60'
                  : r.kind === 'sectionTotal' ? 'border-t border-slate-700/60 bg-slate-800/30' : 'border-b border-slate-800/40 hover:bg-slate-700/20';
                return (
                  <tr key={i} className={rowCls}>
                    <td className={`sticky left-0 bg-slate-900 px-3 py-1.5 whitespace-nowrap ${strong ? 'text-white font-medium' : 'text-slate-300'}`}
                      style={{ paddingLeft: r.kind === 'row' ? `${0.75 + (r.level - 2) * 1.1}rem` : undefined }}>
                      {r.label}
                    </td>
                    {cols.map((_, ci) => <td key={ci} className="px-3 py-1.5 text-right tabular-nums text-slate-200"><Amt v={r.amounts[ci] ?? 0} bold={strong} /></td>)}
                    {showTotalCol && <td className="px-3 py-1.5 text-right tabular-nums text-white border-l border-slate-700/40"><Amt v={r.total} bold /></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
